/**
 * Product card image gallery helpers (every product card in the theme).
 *
 * 1. Arrows change the image, exactly once per click.
 *    snippets/card-gallery.liquid wraps the image slideshow in a link to the
 *    product, and each arrow is a <button> with no type inside it, so a click
 *    could follow the link or submit the cart form instead. On top of that,
 *    assets/component.js delegates the arrows' `on:click="/next"` from a
 *    capture listener on `document`; a second handler there made every click
 *    advance twice. The click is taken over here from a capture listener on
 *    `window`, which runs before anything on `document`, and stopped there.
 *
 *    Pressing an arrow must not start the slideshow's mouse drag either:
 *    assets/slideshow.js treats any pointerdown on the gallery as the start of
 *    a drag, and the slightest mouse movement while the button is held sets
 *    the `dragging` attribute, which hides the arrows (display: none) and
 *    captures the pointer, so the arrow vanished under the cursor and the
 *    click was lost. Pointer presses on the arrows are stopped at `window`.
 *
 * 2. (Arrows with nothing to show are fixed server-side in
 *    snippets/card-gallery.liquid: variant photos are no longer hidden when
 *    that would leave a card on a single photo.)
 *
 * 3. Image progress segments (one per photo, current one in gold).
 *
 * 4. (Removed 30.09: the 3D tilt of the image toward the cursor.)
 *
 * Note on `disabled`: assets/slideshow.js always runs its setter on setup
 * (`this.disabled = this.isNested || this.disabled`), which writes the literal
 * attribute `disabled="false"` on every working gallery, so only the property
 * getter reads it correctly.
 */
const CONTROL_SELECTOR = '.slideshow-control';
const GALLERY_SELECTOR = '.card-gallery';
const COMPONENT_SELECTOR = 'slideshow-component';
const CARD_SELECTOR = 'product-card';
const COMPACT_SELECTOR = '.header, .header-drawer, .predictive-search-results__card';

if (!window.__mokyCardGalleryArrows) {
  window.__mokyCardGalleryArrows = true;

  /** @param {Element} gallery */
  const componentOf = (gallery) => gallery.querySelector(COMPONENT_SELECTOR);

  /** @param {Element} component */
  const allSlides = (component) => Array.from(component.querySelectorAll('slideshow-slide'));

  /** @param {any} component */
  const visibleCount = (component) =>
    Array.isArray(component.slides)
      ? component.slides.length
      : allSlides(component).filter((s) => !s.hidden || s.hasAttribute('reveal')).length;

  /** @param {Element} seg @param {number} index */
  const paintSegments = (seg, index) => {
    Array.from(seg.children).forEach((el, i) => el.classList.toggle('is-on', i === index));
  };

  /** Build or refresh the progress segments for a card gallery. */
  const syncSegments = (gallery) => {
    if (gallery.closest(COMPACT_SELECTOR)) return;
    const component = componentOf(gallery);
    if (!component) return;
    const count = visibleCount(component);
    let seg = gallery.querySelector(':scope > .moky-seg');

    if (count < 2) {
      if (seg) seg.hidden = true;
      return;
    }

    if (!seg) {
      seg = document.createElement('div');
      seg.className = 'moky-seg';
      seg.setAttribute('aria-hidden', 'true');
      gallery.appendChild(seg);
    }

    if (seg.children.length !== count) {
      seg.textContent = '';
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('span');
        dot.className = 'moky-seg__i';
        seg.appendChild(dot);
      }
    }

    seg.hidden = false;
    const current = Number(component.current) || 0;
    paintSegments(seg, Math.min(Math.max(current, 0), count - 1));
  };

  /** Prepare a card the first time the shopper reaches it. */
  const prepareCard = (card) => {
    const gallery = card.querySelector(GALLERY_SELECTOR);
    if (!gallery) return;
    const component = componentOf(gallery);
    if (!component) return;
    syncSegments(gallery);
  };

  // ---- 1. Arrow clicks -------------------------------------------------
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const control = target.closest(CONTROL_SELECTOR);
      if (!control) return;

      const gallery = control.closest(GALLERY_SELECTOR);
      if (!gallery) return;

      const component = control.closest(COMPONENT_SELECTOR);
      if (!component) return;

      // Never navigate, never submit a form, and never let the theme's
      // delegated on:click handler run the same action a second time.
      event.preventDefault();
      event.stopImmediatePropagation();

      if (component.disabled) component.disabled = false;

      const action = control.classList.contains('slideshow-control--next') ? 'next' : 'previous';
      if (typeof component[action] !== 'function') return;

      component[action](event);
      syncSegments(gallery);
    },
    true
  );

  // Keep the slideshow's drag handler away from presses on the arrows.
  window.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const control = target.closest(CONTROL_SELECTOR);
      if (!control || !control.closest(GALLERY_SELECTOR)) return;
      event.stopImmediatePropagation();
    },
    true
  );

  // ---- 3. Keep the segments in step with the slideshow (arrows, swipes, drags)
  document.addEventListener('slideshow:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const gallery = target.closest(GALLERY_SELECTOR);
    if (!gallery || !gallery.closest(CARD_SELECTOR)) return;
    syncSegments(gallery);
    const seg = gallery.querySelector(':scope > .moky-seg');
    const index = event.detail && typeof event.detail.index === 'number' ? event.detail.index : null;
    if (seg && index !== null) paintSegments(seg, index);
  });

  document.addEventListener(
    'pointerover',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const card = target.closest(CARD_SELECTOR);
      if (!card || card.dataset.mokyPrepared === 'true') return;
      card.dataset.mokyPrepared = 'true';
      prepareCard(card);
    },
    { passive: true }
  );

  // Touch screens have no hover: prepare cards as they scroll into view.
  if ('IntersectionObserver' in window && window.matchMedia('(any-pointer: coarse)').matches) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const card = /** @type {HTMLElement} */ (entry.target);
          io.unobserve(card);
          if (card.dataset.mokyPrepared === 'true') return;
          card.dataset.mokyPrepared = 'true';
          prepareCard(card);
        });
      },
      { rootMargin: '120px 0px' }
    );
    const observeAll = () => document.querySelectorAll(CARD_SELECTOR).forEach((card) => io.observe(card));
    if (document.readyState === 'complete') observeAll();
    else window.addEventListener('load', observeAll);
  }
}
